import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

import { env } from './env.js';
import { logger } from './logger.js';

/**
 * Initialise App Check on this Firebase app.
 *
 * - Prod: requires VITE_RECAPTCHA_SITE_KEY (reCAPTCHA Enterprise).
 * - Dev (useEmulators=true): sets the global debug-token flag so the emulator
 *   accepts requests without a real reCAPTCHA challenge.
 *
 * Call ONCE at boot, immediately after `initializeApp`, BEFORE any callable
 * invocation.
 */
/**
 * reCAPTCHA cannot issue a token for an origin that is not on the site key's
 * domain allow-list, and localhost never is. Prerendering runs a headless
 * browser against a local server, so initialising App Check there produced one
 * console error per page (104 of them) and failed the marketing build; local
 * `vite dev` produced the same error on every reload.
 *
 * App Check exists to attest a real browser on a real deployed origin. On
 * localhost there is nothing to attest, so this skips rather than failing
 * loudly about a condition that is entirely expected.
 */
function isLocalOrigin(): boolean {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local');
}

export function initAppCheck(firebaseApp: FirebaseApp): void {
  if (isLocalOrigin()) return;
  if (env.useEmulators) {
    (self as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  if (!env.recaptchaSiteKey) {
    if (!env.useEmulators) {
      logger.warn(
        'App Check: VITE_RECAPTCHA_SITE_KEY missing; callables enforcing App Check will fail',
      );
    }
    return;
  }

  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(env.recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    logger.error('App Check init failed', { error: String(e) });
  }
}
